import i18next from 'i18next'
import {
	createBrowserRouter,
	createRoutesFromElements,
	Navigate,
	Route,
	useLocation
} from 'react-router'
import { ErrorBox } from '@/components/commons/error-box'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { AlertCircleIcon } from '@/components/ui/icons'
import { envVars } from '@/env-vars'
import { MarqueeEventTabRedirect } from '@/features/marquee/components/marquee-event-tab-redirect'
import { LuxeSubmissionsCsvUploadsLayout } from '@/layouts/luxe-submissions-csv-uploads-layout'
import { LuxeSubmissionsLayout } from '@/layouts/luxe-submissions-layout'
import { LuxeSubmissionsMMOverviewLayout } from '@/layouts/luxe-submissions-mm-overview-layout'
import { LuxeSubmissionsQueueLayout } from '@/layouts/luxe-submissions-queue-layout'
import { MarqueeEventDetailLayout } from '@/layouts/marquee-event-detail-layout'
import { MarqueeFormLayout } from '@/layouts/marquee-form-layout'
import { MarqueeLayout } from '@/layouts/marquee-layout'
import { PhotosetLayout } from '@/layouts/photoset-layout'
import { PrivateLayout } from '@/layouts/private-layout'
import { PrivateVettingLayout } from '@/layouts/private-vetting-layout'
import { PublicLayout } from '@/layouts/public-layout'
import { ExperienceProPhotosRoute } from '@/pages/experience-pro-photos-route'
import { ExperienceVetting } from '@/pages/experience-vetting'
import { ServiceProPhotosRoute } from '@/pages/service-pro-photos-route'
import { ServiceVetting } from '@/pages/service-vetting'
import { LuminateSessionRoute } from '@/route-guards/luminate-session-route'
import { LuxeSubmissionsQueueRoute } from '@/route-guards/luxe-submissions-queue-route'
import { LuxeSubmissionsRoute } from '@/route-guards/luxe-submissions-route'
import { MarqueeRoute } from '@/route-guards/marquee-route'
import { MarqueeSessionRoute } from '@/route-guards/marquee-session-route'
import { Photoset2LaRoute } from '@/route-guards/photoset-2la-route'
import { PhotosetEditingRoute } from '@/route-guards/photoset-editing-route'
import { PhotosetQaRoute } from '@/route-guards/photoset-qa-route'
import { PhotosetRoute } from '@/route-guards/photoset-route'
import { PrivateRoute } from '@/route-guards/private-route'
import { PublicRoute } from '@/route-guards/public-route'
import { RoleBasedRouteGuard } from '@/route-guards/role-based-route-guard'
import { routes } from '@/routes'
import {
	FULL_ACCESS_USER_ROLES,
	OPS_ACCESS_USER_ROLES
} from './features/luxe-submissions/utils'

function QuoteGenerationRedirect() {
	const { search } = useLocation()

	return (
		<Navigate
			replace
			to={{
				pathname:
					routes.LUXE_SUBMISSIONS_QUOTE_GENERATION.handle.fullPath,
				search
			}}
		/>
	)
}

const router = createBrowserRouter(
	createRoutesFromElements(
		<Route>
			{/* Everything outside Marquee resolves the Luminate session first: the
			    role guards below decide on `user.role` and cannot decide without
			    it. Marquee is a sibling and resolves its own operator instead. */}
			<Route element={<LuminateSessionRoute />}>
				{/* Authenticated user routes */}
				<Route element={<PrivateRoute />}>
					<Route {...routes.WELCOME} />
					<Route {...routes.EXPERIENCES_SHELL} />
					<Route {...routes.EXPERIENCES_SHELL_UPGRADE} />
					<Route {...routes.EXPERIENCE_VIEW_ONLY} />

					{/* SubGeo Territory Builder Route */}
					<Route element={<PrivateLayout />}>
						<Route {...routes.SUBGEO_TERRITORY_BUILDER} />
						<Route {...routes.GEOMETRY_CHECK} />
					</Route>

					{/* Roadmaps */}
					<Route element={<PrivateLayout />}>
						<Route {...routes.ROADMAP_2026} />
					</Route>

					{/* Host review - full rejections (read-only team view) */}
					<Route element={<PrivateLayout />}>
						<Route {...routes.HOST_REVIEW_FULL_REJECTIONS} />
						<Route {...routes.HOST_REVIEW_FULL_REJECTION_DETAIL} />
					</Route>

					<Route element={<PrivateLayout />}>
						<Route element={<PhotosetRoute />}>
							<Route element={<PhotosetLayout />}>
								<Route element={<PhotosetEditingRoute />}>
									<Route {...routes.PHOTOSET_REJECTED} />
									<Route {...routes.PHOTOSET_INITIAL_QA} />
									<Route {...routes.PHOTOSET_ALLOCATION} />
									<Route {...routes.PHOTOSET_CURATION} />
									<Route
										{...routes.PHOTOSET_CURATION_COMPLETED}
									/>
									<Route {...routes.PHOTOSET_AI_VETTING} />
									<Route
										{...routes.PHOTOSET_AI_VETTING_COMPLETED}
									/>
									<Route
										{...routes.PHOTOSET_AI_MANUAL_RETOUCHING_UPLOAD}
									/>
									<Route
										{...routes.PHOTOSET_AI_MANUAL_RETOUCHING}
									/>
									<Route {...routes.PHOTOSET_NEEDS_REVIEW} />
									<Route
										{...routes.PHOTOSET_RETOUCHING_REVISION_UPLOAD}
									/>
									<Route
										{...routes.PHOTOSET_RETOUCHING_REVISION}
									/>
									<Route
										{...routes.PHOTOSET_RETOUCHING_REVISION_APPROVED}
									/>
								</Route>
								<Route element={<PhotosetQaRoute />}>
									<Route
										{...routes.PHOTOSET_UNAVAILABLE_QA}
									/>
									<Route
										{...routes.PHOTOSET_EDITING_QA_SELECTION}
									/>
									<Route
										{...routes.PHOTOSET_EDITING_QA_FLAGS}
									/>
									<Route
										{...routes.PHOTOSET_EDITING_QA_COMPLETED}
									/>
									<Route {...routes.PHOTOSET_VETTING_QA} />
									<Route {...routes.PHOTOSET_RETOUCHING_QA} />
									<Route
										{...routes.PHOTOSET_RETOUCHING_QA_COMPLETED}
									/>
								</Route>
								<Route element={<Photoset2LaRoute />}>
									<Route {...routes.PHOTOSET_2LA_INDEX} />
									<Route
										{...routes.PHOTOSET_UNAVAILABLE_2LA}
									/>
									<Route
										{...routes.PHOTOSET_2LA_EDITING_QA_SELECTION}
									/>
									<Route
										{...routes.PHOTOSET_2LA_EDITING_QA_FLAGS}
									/>
									<Route
										{...routes.PHOTOSET_2LA_VETTING_QA}
									/>
									<Route
										{...routes.PHOTOSET_2LA_RETOUCHING_QA}
									/>
								</Route>
							</Route>
						</Route>
					</Route>
				</Route>
				{/* Anonymous user routes */}
				<Route
					element={<PublicRoute />}
					errorElement={
						<ErrorBox
							cta={
								<ButtonBrandLegacy
									onClick={() => window.location.reload()}
								>
									{i18next.t('common:reload_the_page')}
								</ButtonBrandLegacy>
							}
							icon={<AlertCircleIcon />}
							title={i18next.t('errors:default')}
						/>
					}
				>
					<Route element={<PublicLayout />}>
						<Route {...routes.LOGIN} />
						<Route {...routes.SUBGEO_LOGIN} />
						<Route {...routes.SSO_REDIRECT} />
						<Route {...routes.RESET_PASSWORD} />
						<Route {...routes.SET_PASSWORD} />
					</Route>
				</Route>

				{/* Copy Preparation/Training AI public route */}
				<Route
					element={<PublicRoute />}
					errorElement={
						<ErrorBox
							cta={
								<ButtonBrandLegacy
									onClick={() => window.location.reload()}
								>
									{i18next.t('common:reload_the_page')}
								</ButtonBrandLegacy>
							}
							icon={<AlertCircleIcon />}
							title={i18next.t('errors:default')}
						/>
					}
				>
					<Route element={<PrivateVettingLayout />}>
						<Route {...routes.EXP_AI_COPY_PREP_SHELL_TRAINING} />
						<Route {...routes.EXP_AI_COPY_PREP_VETTING_TRAINING} />
						<Route {...routes.SERVICE_COPY_TRAINING} />
					</Route>
				</Route>

				{/* OPS Vetting private route */}
				<Route
					element={<PrivateRoute />}
					errorElement={
						<ErrorBox
							cta={
								<ButtonBrandLegacy
									onClick={() => window.location.reload()}
								>
									{i18next.t('common:reload_the_page')}
								</ButtonBrandLegacy>
							}
							icon={<AlertCircleIcon />}
							title={i18next.t('errors:default')}
						/>
					}
				>
					<Route element={<PrivateVettingLayout />}>
						<Route element={<ServiceVetting />}>
							<Route {...routes.SERVICE_VETTING_MERCHANDISING} />
							<Route {...routes.SERVICE_VETTING_CRITERIA} />
						</Route>

						<Route element={<ExperienceVetting />}>
							<Route
								{...routes.EXPERIENCE_VETTING_MERCHANDISING}
							/>
							<Route {...routes.EXPERIENCE_VETTING_CRITERIA} />
						</Route>

						<Route element={<ExperienceProPhotosRoute />}>
							<Route {...routes.EXPERIENCES_IMG_REORDERING} />
						</Route>

						<Route element={<ServiceProPhotosRoute />}>
							<Route {...routes.SERVICE_IMG_REORDERING} />
						</Route>
					</Route>
				</Route>

				{/* Temporary public routes for initial QA v1 and curation v1 */}
				<Route
					element={<PhotosetRoute />}
					errorElement={
						<ErrorBox
							cta={
								<ButtonBrandLegacy
									onClick={() => window.location.reload()}
								>
									{i18next.t('common:reload_the_page')}
								</ButtonBrandLegacy>
							}
							icon={<AlertCircleIcon />}
							title={i18next.t('errors:default')}
						/>
					}
				>
					<Route element={<PhotosetLayout />}>
						<Route {...routes.PHOTOSET_INITIAL_QA_V1} />
						<Route {...routes.PHOTOSET_CURATION_STEP_V1} />
						<Route {...routes.PHOTOSET_IMAGEN_AI_STEP_V1} />
						<Route {...routes.PHOTOSET_PUBLIC_SHOWCASE} />
					</Route>
				</Route>

				{/* Luxe Submissions Routes */}
				{envVars.VITE_IS_LUXE_ENABLED && (
					<Route path='luxe-submissions'>
						<Route element={<PublicRoute />}>
							<Route element={<PublicLayout />}>
								<Route {...routes.LUXE_SUBMISSIONS_LOGIN} />
							</Route>
						</Route>

						<Route element={<LuxeSubmissionsRoute />}>
							<Route element={<LuxeSubmissionsLayout />}>
								<Route {...routes.LUXE_SUBMISSIONS_INDEX} />

								<Route
									element={
										<RoleBasedRouteGuard
											allowedRoles={['marketing_manager']}
										/>
									}
								>
									<Route path='submission'>
										<Route
											index
											{...routes.LUXE_SUBMISSIONS_SUBMISSION}
										/>
										<Route
											{...routes.LUXE_SUBMISSIONS_SUBMISSION_LISTING}
										/>
										<Route
											{...routes.LUXE_SUBMISSIONS_SUBMISSION_OTHER}
										/>
									</Route>
								</Route>

								<Route path='queue'>
									<Route
										element={<LuxeSubmissionsQueueRoute />}
										index
									/>
									<Route
										element={<LuxeSubmissionsQueueLayout />}
									>
										<Route
											element={
												<RoleBasedRouteGuard
													allowedRoles={
														OPS_ACCESS_USER_ROLES
													}
												/>
											}
										>
											<Route
												{...routes.LUXE_SUBMISSIONS_INITIAL_EVALUATIONS}
											/>
											<Route
												{...routes.LUXE_SUBMISSIONS_FINAL_REVIEW}
											/>
										</Route>

										<Route
											element={
												<RoleBasedRouteGuard
													allowedRoles={
														FULL_ACCESS_USER_ROLES
													}
												/>
											}
										>
											<Route
												element={
													<QuoteGenerationRedirect />
												}
												path='quote-generation'
											/>
											<Route
												{...routes.LUXE_SUBMISSIONS_QUOTE_GENERATION}
											/>
										</Route>

										<Route
											element={
												<RoleBasedRouteGuard
													allowedRoles={[
														...FULL_ACCESS_USER_ROLES,
														'luxe_ops',
														'luxe_vetter'
													]}
												/>
											}
										>
											<Route
												{...routes.LUXE_SUBMISSIONS_QUEUE_VISUAL_VETTING}
											/>
										</Route>
									</Route>
								</Route>

								<Route path='mm-overview'>
									<Route
										element={
											<LuxeSubmissionsMMOverviewLayout />
										}
									>
										<Route
											element={
												<RoleBasedRouteGuard
													allowedRoles={[
														...FULL_ACCESS_USER_ROLES, //view only
														'marketing_manager' //full access
													]}
												/>
											}
										>
											<Route
												{...routes.LUXE_SUBMISSIONS_MM_SUBMISSIONS}
											/>
										</Route>
										<Route
											element={
												<RoleBasedRouteGuard
													allowedRoles={[
														'marketing_manager'
													]}
												/>
											}
										>
											<Route
												{...routes.LUXE_SUBMISSIONS_MM_PARKING_LOT}
											/>
											<Route
												{...routes.LUXE_SUBMISSIONS_MM_DATA_OVERVIEW}
											/>
										</Route>
									</Route>

									<Route
										element={
											<RoleBasedRouteGuard
												allowedRoles={[
													...FULL_ACCESS_USER_ROLES, //view only
													'marketing_manager' //full access
												]}
											/>
										}
									>
										<Route
											{...routes.LUXE_SUBMISSIONS_MM_SUBMISSION_DETAILS}
										/>
										<Route
											{...routes.LUXE_SUBMISSIONS_MM_PARKING_LOT_DETAILS}
										/>
									</Route>
								</Route>

								<Route
									element={
										<RoleBasedRouteGuard
											allowedRoles={[
												...FULL_ACCESS_USER_ROLES,
												'luxe_vetter',
												'luxe_ops'
											]}
										/>
									}
								>
									<Route
										{...routes.LUXE_SUBMISSIONS_LISTING}
									/>
								</Route>

								<Route
									element={
										<RoleBasedRouteGuard
											allowedRoles={OPS_ACCESS_USER_ROLES}
										/>
									}
								>
									<Route
										{...routes.LUXE_SUBMISSIONS_LISTING_FINAL_REVIEW}
									/>
								</Route>

								<Route
									element={
										<RoleBasedRouteGuard
											allowedRoles={[
												...FULL_ACCESS_USER_ROLES,
												'luxe_ops',
												'luxe_vetter'
											]}
										/>
									}
								>
									<Route
										{...routes.LUXE_SUBMISSIONS_LISTING_SCORE_FORM}
									/>
								</Route>

								<Route
									element={
										<RoleBasedRouteGuard
											allowedRoles={[
												...FULL_ACCESS_USER_ROLES,
												'marketing_manager'
											]}
										/>
									}
									path='csv-uploads'
								>
									<Route
										element={
											<LuxeSubmissionsCsvUploadsLayout />
										}
									>
										<Route
											{...routes.LUXE_SUBMISSIONS_CSV_UPLOADS}
										/>
									</Route>
								</Route>

								<Route
									element={
										<RoleBasedRouteGuard
											allowedRoles={[
												...FULL_ACCESS_USER_ROLES,
												'marketing_manager'
											]}
											flaggedRoles={{
												flag: 'luxe-vv2-acc-active-assignments',
												roles: ['luxe_vetter']
											}}
										/>
									}
								>
									<Route
										{...routes.LUXE_SUBMISSIONS_TEAM_PRODUCTIVITY}
									/>
									<Route
										{...routes.LUXE_SUBMISSIONS_MP_CONVERSION}
									/>
								</Route>
							</Route>
						</Route>
					</Route>
				)}
			</Route>

			{/* Marquee Routes */}
			<Route path='marquee'>
				<Route element={<PublicRoute />}>
					<Route element={<PublicLayout />}>
						<Route {...routes.MARQUEE_LOGIN} />
					</Route>
				</Route>

				<Route element={<MarqueeRoute />}>
					<Route element={<MarqueeSessionRoute />}>
						<Route element={<MarqueeLayout />}>
							<Route
								element={
									<Navigate
										replace
										to={
											routes.MARQUEE_EVENTS.handle
												.fullPath
										}
									/>
								}
								index
							/>
						</Route>

						<Route element={<MarqueeLayout />}>
							<Route {...routes.MARQUEE_HOST_LOOKUP_TABLE} />
						</Route>

						{/* The section root as a crumb ("Marquee › Team",
						    "Marquee › Location"): it
						    resolves to the tracker through the index redirect. */}
						<Route
							element={<MarqueeLayout />}
							handle={{
								fullPath: '/marquee',
								breadcrumb: 'marquee:breadcrumb_marquee'
							}}
						>
							<Route {...routes.MARQUEE_TEAM} />
							<Route {...routes.MARQUEE_LOCATIONS} />
						</Route>

						<Route
							handle={{
								fullPath: '/marquee/events',
								breadcrumb: 'marquee:breadcrumb_event_list'
							}}
							path='events'
						>
							{/* Create / edit form: body scroll + sticky header */}
							<Route element={<MarqueeFormLayout />}>
								<Route {...routes.MARQUEE_EVENT_NEW} />
								<Route {...routes.MARQUEE_EVENT_EDIT} />
							</Route>

							{/* List + detail: app shell */}
							<Route element={<MarqueeLayout />}>
								<Route {...routes.MARQUEE_EVENTS} />

								<Route {...routes.MARQUEE_EVENT_DETAIL}>
									<Route
										element={<MarqueeEventTabRedirect />}
										index
									/>
									<Route
										element={<MarqueeEventDetailLayout />}
									>
										<Route
											{...routes.MARQUEE_EVENT_ATTENDEES}
										/>
										<Route
											{...routes.MARQUEE_EVENT_CHECK_IN}
										/>
										<Route
											{...routes.MARQUEE_EVENT_EXPORT}
										/>
										<Route
											{...routes.MARQUEE_EVENT_REPORT}
										/>
									</Route>
									<Route
										{...routes.MARQUEE_EVENT_ATTENDEE_DETAIL}
									/>
								</Route>
							</Route>
						</Route>
					</Route>
				</Route>
			</Route>

			<Route {...routes.NOT_FOUND} />
		</Route>
	)
)

export default router
